import { useState } from "react";
import type { CanonStatus, ContinuityIssue } from "../domain/types";
import { mergeDetectedIssues } from "../domain/continuity";
import { Field, Modal, SourceFields, StatusBadge, nowIso, submitGuard } from "../components/ui";
import { useStore } from "../store";

export function ContinuityPage() {
  const { bible, updateBible } = useStore();
  const [adding, setAdding] = useState(false);
  if (!bible) return null;
  const issues = mergeDetectedIssues(bible);
  const resolve = (id: string, resolution: string, state: ContinuityIssue["state"]) => {
    updateBible((draft) => ({
      ...draft,
      continuityIssues: draft.continuityIssues.some((i) => i.id === id)
        ? draft.continuityIssues.map((i) => (i.id === id ? { ...i, state, resolution, updatedAt: nowIso() } : i))
        : [...draft.continuityIssues, { ...issues.find((i) => i.id === id)!, state, resolution, updatedAt: nowIso() }],
    }), `Décision continuité ${id}`);
  };
  return (
    <div>
      <div className="row space">
        <h1>Continuité</h1>
        <button className="btn primary" type="button" onClick={() => setAdding(true)}>Signaler</button>
      </div>
      <p className="tiny">Les alertes n’empêchent pas d’écrire. Rien n’est tranché silencieusement.</p>
      <div className="grid">
        {issues.map((i) => (
          <article key={i.id} className="card">
            <div className="row space">
              <h3>{i.title}</h3>
              <span className={`badge ${i.severity}`}>{i.severity}</span>
            </div>
            <StatusBadge status={i.state} />
            <p>{i.description}</p>
            <p className="tiny">Entités : {i.entityRefs.join(", ")} {i.momentIds?.length ? `· ${i.momentIds.join(", ")}` : ""}</p>
            {i.resolution && <p>Décision : {i.resolution}</p>}
            {(i.state === "open" || i.state === "decision-needed" || i.state === "investigating") && (
              <div className="row">
                <button className="btn" type="button" onClick={() => {
                  const resolution = prompt("Décision retenue (obligatoire) :");
                  if (!resolution) return;
                  resolve(i.id, resolution, "resolved");
                }}>Marquer résolu</button>
                <button className="btn" type="button" onClick={() => {
                  const resolution = prompt("Pourquoi ce cas est-il toléré ?");
                  if (!resolution) return;
                  resolve(i.id, resolution, "accepted");
                }}>Toléré volontairement</button>
              </div>
            )}
          </article>
        ))}
      </div>
      <IssueForm
        open={adding}
        onClose={() => setAdding(false)}
        onCreate={(issue) => {
          updateBible((d) => ({ ...d, continuityIssues: [...d.continuityIssues, issue] }), `Alerte ${issue.title}`);
          setAdding(false);
        }}
      />
    </div>
  );
}

function IssueForm({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (i: ContinuityIssue) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<CanonStatus>("verify");
  const [sourceLabel, setSourceLabel] = useState("");
  return (
    <Modal open={open} title="Nouveau problème" onClose={onClose}>
      <form
        className="grid"
        onSubmit={(e) => {
          if (!submitGuard(e, status, sourceLabel)) return;
          onCreate({
            id: `issue-${Date.now().toString(36)}`,
            status,
            sources: sourceLabel ? [{ label: sourceLabel }] : [{ label: "Saisie auteur" }],
            createdAt: nowIso(),
            updatedAt: nowIso(),
            severity: "attention",
            state: "open",
            title,
            description,
            entityRefs: [],
          });
        }}
      >
        <Field label="Titre"><input value={title} onChange={(e) => setTitle(e.target.value)} required /></Field>
        <Field label="Description"><textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} required /></Field>
        <SourceFields status={status} sourceLabel={sourceLabel} setStatus={setStatus} setSourceLabel={setSourceLabel} />
        <button className="btn primary" type="submit">Ajouter</button>
      </form>
    </Modal>
  );
}
